/**
 * Textos do LeadExplorer e do Fale conosco (proposta "LeadExplorer & Contact", 17/10/2026).
 * O português fica como padrão no código (chaves `lx_*` em src/i18n/locales).
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../lib/format';
import { localeOf } from '../home/homeText';
import { asText, toDate } from './resultModel';

export const LX_PT = {
  title: 'LeadExplorer',
  close: 'Fechar',
  intro:
    'Digite um CNPJ ou um site e veja quem é a empresa, seus contatos públicos e como abordá-la.',
  intro_site: 'Digite um site e veja quem é a empresa, seus contatos públicos e como abordá-la.',
  label: 'CNPJ ou site',
  label_site: 'Site',
  placeholder: '11.222.333/0001-81 ou nomedosite.com.br',
  placeholder_site: 'nomedosite.com.br',
  chip_cnpj: 'CNPJ',
  chip_site: 'Site',
  analyze: 'Analisar',
  ok_cnpj: 'Vamos analisar o CNPJ {{value}}',
  ok_site: 'Vamos analisar o site {{value}}',
  missing_one: 'Falta 1 número para completar o CNPJ.',
  missing_other: 'Faltam {{n}} números para completar o CNPJ.',
  bad_cnpj: 'Esse CNPJ não existe. Confira os números.',
  bad_term: 'Digite um CNPJ ou um site, como nomedosite.com.br.',
  bad_term_site: 'Digite um site, como nomedosite.com.',
  emi_cnpj: 'No eMiner, a análise é feita pelo site. Digite um endereço, como nomedosite.com.',
  disclaimer: 'Dados públicos (Receita, Whois, Google e redes). Podem ter imprecisões.',
  disclaimer_site: 'Dados públicos (Whois, Google e redes). Podem ter imprecisões.',

  err_not_found_cnpj: 'Não encontramos nada sobre esse CNPJ.',
  err_not_found_site: 'Não encontramos nada sobre esse site.',
  err_unavailable: 'O LeadExplorer está indisponível agora. Tente de novo em alguns minutos.',
  err_domain_check: 'Não foi possível verificar o site agora. Tente de novo.',
  err_failed: 'Não foi possível concluir a análise. Tente de novo.',
  err_limit: 'Muitas consultas seguidas. Espere alguns minutos e tente de novo.',
  err_quota: 'Você já usou as análises do seu plano neste período. Elas renovam {{when}}.',
  err_quota_soon: 'Você já usou as análises do seu plano neste período.',
  quota_left_one: 'Resta 1 análise {{window}} · renova {{when}}',
  quota_left_other: 'Restam {{n}} análises {{window}} · renova {{when}}',
  quota_none: 'As análises {{window}} acabaram · renovam {{when}}',
  quota_day: 'hoje',
  quota_week: 'nesta semana',
  sample_title: 'Amostra do dossiê',
  sample_text: 'Os dados aparecem embaralhados. Com o LeadExplorer completo, você vê a empresa inteira e a análise por IA.',
  sample_cta: 'Ver planos',

  analyzing_cnpj: 'Analisando <b>CNPJ {{value}}</b>',
  analyzing_site: 'Analisando <b>{{value}}</b>',
  st_domain: 'Domínio no registro.br',
  st_public: 'Dados públicos do site',
  st_rf: 'Receita Federal',
  st_stack: 'Tecnologias, redes e estatísticas',
  st_build: 'Montando o resultado',
  run_domain: 'Consultando o registro do domínio',
  run_public: 'Lendo a página inicial do site',
  run_rf: 'Consultando o cadastro do CNPJ',
  run_stack: 'Buscando tecnologias, redes sociais e visitas do site',
  run_build: 'Organizando o resultado',
  done_registered: 'Registrado em {{date}}',
  done_public: 'Página inicial lida',
  done_cnpj_registry: 'CNPJ {{cnpj}}, do registro do domínio',
  done_cnpj_page: 'CNPJ {{cnpj}}, escrito no próprio site',
  done_techs_one: '1 tecnologia encontrada',
  done_techs_other: '{{n}} tecnologias encontradas',
  done: 'Concluída',
  na_not_br: 'Não se aplica: só para sites .com.br',
  na_no_cnpj: 'Não se aplica: o site não mostrou um CNPJ',
  na_cpf_owner: 'Não se aplica: o domínio está no CPF de uma pessoa',
  state_done: '(concluída)',
  state_running: '(em andamento)',
  state_todo: '(a fazer)',
  state_na: '(não se aplica)',
  cancel: 'Cancelar análise',
  cancel_title: 'Cancelar a análise?',
  cancel_text: 'A análise para agora e nada dela aparece. Você pode começar outra quando quiser.',
  keep: 'Continuar analisando',

  new: 'Nova análise',
  rf_active: 'Ativa na Receita Federal',
  rf_status: '{{status}} na Receita Federal',
  opened_on: 'Aberta em {{date}}',
  socials_one: '1 rede social',
  socials_other: '{{n}} redes sociais',
  na_title: 'Não se aplica a este site',
  na_rf: 'Receita Federal: o site não mostrou um CNPJ.',
  na_rf_cpf:
    'Receita Federal: o domínio está registrado no CPF de uma pessoa, e o site não mostrou um CNPJ.',
  na_domain: 'Domínio: só para sites .com.br.',
  copy: 'Copiar',
  tab_error: 'Não conseguimos mostrar esta parte da análise. As outras abas continuam disponíveis.',
  copied: 'Copiado',

  tab_strategy: 'Estratégia de prospecção',
  tab_rf: 'Receita Federal',
  tab_domain: 'Domínio',
  tab_social: 'Redes sociais',
  tab_site: 'Site',
  tab_tech: 'Tecnologias',
  // Grupo Econômico (decisões 305 a 316).
  tab_group: 'Grupo Econômico',
  gr_sub: 'Empresas e pessoas ligadas pelos sócios na Receita Federal, competência {{month}}.',
  gr_sub_nomonth: 'Empresas e pessoas ligadas pelos sócios na Receita Federal.',
  gr_note: 'Não é o grupo econômico no sentido da lei: são ligações pelos sócios. A Receita não informa o percentual de cada sócio e, nas S.A., traz os administradores, não os acionistas.',
  gr_loading: 'Buscando as empresas ligadas…',
  gr_error: 'Não conseguimos montar o grupo agora.',
  gr_empty: 'Nenhuma outra empresa ligada pelos sócios.',
  gr_owners: 'Sócios e donos',
  gr_admins: 'Administração',
  gr_admins_hint: 'Diretores, presidentes e outros cargos. Não são donos, e o grupo não segue por eles.',
  gr_controlled: 'Participações em outras empresas',
  gr_related: 'Outras empresas dos mesmos sócios',
  gr_since: 'desde {{year}}',
  gr_via: 'Ligada por {{name}}',
  gr_probable: 'Provável mesma pessoa',
  gr_probable_hint: 'Mesmo nome e mesmos dígitos do CPF que a Receita mostra.',
  gr_cut: 'Mostrando {{shown}} de {{total}}.',
  gr_closed_show_one: 'Mostrar 1 encerrada',
  gr_closed_show_other: 'Mostrar {{n}} encerradas',
  gr_closed_hide: 'Esconder encerradas',
  gr_ficha: 'Ver ficha',
  gr_ficha_hide: 'Fechar ficha',
  gr_ficha_loading: 'Carregando a ficha…',
  gr_ficha_error: 'Não conseguimos abrir a ficha agora.',
  gr_branches_one: '1 filial',
  gr_branches_other: '{{n}} filiais',
  gr_recenter: 'Ver o grupo',
  gr_back: 'Voltar para {{name}}',
  gr_back_root: 'Voltar à empresa analisada',
  gr_showing: 'Grupo de {{name}}',
  gr_analyze: 'Analisar',
  gr_analyze_label: 'Analisar {{name}} no LeadExplorer',
  gr_analyze_hint: 'Roda o LeadExplorer neste CNPJ, com todos os detalhes. Gasta uma análise.',
  gr_person_more: 'Outras empresas',
  gr_person_less: 'Esconder empresas',
  gr_person_none: 'Nenhuma outra empresa com esta pessoa.',
  gr_foreign: 'Sócio no exterior',
  // Gráfico de rede (decisão 319).
  gr_view_label: 'Visualização',
  gr_view_graph: 'Gráfico',
  gr_view_list: 'Lista',
  gr_graph_label:
    'Gráfico com {{companies}} empresas e {{people}} pessoas ligadas. A lista mostra o mesmo conteúdo.',
  gr_graph_loading: 'Montando o gráfico…',
  gr_zoom_in: 'Aproximar',
  gr_zoom_out: 'Afastar',
  gr_fit: 'Centralizar',
  gr_hint:
    'Clique numa empresa ou pessoa para ver os detalhes. Arraste para mover; para aproximar, use Ctrl + rolagem, o gesto de pinça ou os botões.',
  gr_legend: 'Legenda',
  gr_legend_center: 'Empresa analisada',
  gr_legend_company: 'Empresa',
  gr_legend_inactive: 'Empresa não ativa',
  gr_legend_person: 'Pessoa',
  gr_legend_owner: 'Sócio (a seta vai para a empresa)',
  gr_legend_admin: 'Administração',
  gr_legend_probable: 'Provável mesma pessoa',
  gr_expand: 'Expandir ligações',
  gr_expanding: 'Buscando…',
  gr_close_card: 'Fechar detalhes',
  gr_edge_out: '{{role}} em {{name}}',
  gr_edge_in: '{{name}} · {{role}}',
  gr_linked: 'Ligada',
  gr_cut_named: '{{name}}: mostrando {{shown}} de {{total}}.',

  strategy_sub: 'Escrita por IA a partir de dados públicos. Confira antes de usar.',
  strategy_copy: 'Copiar dossiê',

  // Dossiê com IA sob demanda (decisões 369 a 378), na aba "Estratégia de prospecção".
  dossier_title: 'Dossiê com IA',
  dossier_empty:
    'Um documento só com o que a internet diz sobre a empresa e como abordá-la, escrito com os dados desta análise.',
  dossier_generate: 'Gerar dossiê com IA',
  dossier_regenerate: 'Gerar de novo',
  dossier_legacy:
    'Feito antes do dossiê com IA: a estratégia e a pesquisa ficam como foram geradas.',
  dossier_at: 'Gerado em {{date}}',
  dossier_outdated: 'Gerado antes da última atualização dos dados.',
  dossier_regen_after: 'Dá para gerar de novo a partir de {{date}}.',
  dossier_running: 'Gerando o dossiê',
  dossier_phase_queued: 'Na fila',
  dossier_phase_searching: 'Pesquisando a empresa na internet',
  dossier_phase_reporting: 'Escrevendo o dossiê',
  dossier_phase_finalizing: 'Organizando o dossiê',
  dossier_running_hint:
    'Leva cerca de meio minuto. Você pode ver as outras abas; se fechar, o dossiê fica pronto no histórico.',
  dossier_error_title: 'Não deu para gerar o dossiê',
  dossier_error_text: 'Tente de novo em alguns minutos. Nada foi gasto.',
  dossier_retry: 'Tentar de novo',
  dossier_quota_left_one: 'Resta 1 dossiê {{window}} · renova {{when}}',
  dossier_quota_left_other: 'Restam {{n}} dossiês {{window}} · renova {{when}}',
  dossier_quota_none: 'Os dossiês {{window}} acabaram · renovam {{when}}',
  dossier_err_quota: 'Você já usou os dossiês do seu plano neste período. Eles renovam {{when}}.',
  dossier_err_quota_soon: 'Você já usou os dossiês do seu plano neste período.',
  dossier_err_limit: 'Você já refez muitos dossiês hoje. Amanhã dá para refazer de novo.',
  dossier_locked_title: 'Dossiê com IA fora do seu plano',
  dossier_locked_text:
    'A pesquisa na internet e a estratégia de prospecção fazem parte do LeadExplorer completo.',
  dossier_about: 'Sobre a empresa',

  pub_segment: 'Segmentação de mercado',
  pub_products: 'Produtos e serviços',
  pub_culture: 'Cultura',
  pub_mvv: 'Missão, visão e valores',
  pub_mission: 'Missão',
  pub_vision: 'Visão',
  pub_values: 'Valores',
  pub_target: 'Mercado-alvo',
  pub_employees: 'Funcionários',
  pub_revenue: 'Faturamento anual',
  pub_news: 'Notícias recentes',

  rf_sub: 'Cadastro do CNPJ na Receita Federal.',
  rf_situation: 'Situação',
  rf_since: 'Desde {{date}}',
  rf_opening: 'Abertura',
  rf_size: 'Porte',
  rf_capital: 'Capital social',
  rf_ident: 'Identificação',
  rf_cnpj: 'CNPJ',
  rf_legal: 'Razão social',
  rf_trade: 'Nome fantasia',
  rf_nature: 'Natureza jurídica',
  rf_simples: 'Simples Nacional',
  rf_mei: 'MEI',
  rf_opting: 'Optante',
  rf_not_opting: 'Não optante',
  rf_contact: 'Contato',
  rf_phone: 'Telefone',
  rf_email: 'Email',
  rf_address: 'Endereço',
  rf_cep: 'CEP {{cep}}',
  rf_cnae: 'Atividades econômicas (CNAE)',
  rf_main: 'Principal',
  rf_more_one: 'Mostrar mais 1 atividade',
  rf_more_other: 'Mostrar mais {{n}} atividades',
  rf_less: 'Mostrar menos',
  rf_partners: 'Sócios',
  rf_partner_since: 'Na sociedade desde {{date}}',
  size_mei: 'MEI',
  size_mei_rev: 'Faturamento de até R$ 81 mil por ano',
  size_micro: 'Micro Empresa',
  size_micro_rev: 'Faturamento de até R$ 360 mil por ano',
  size_small: 'Empresa de Pequeno Porte',
  size_small_rev: 'Faturamento de até R$ 4,8 milhões por ano',
  size_other: 'Demais portes',
  size_other_rev: 'Faturamento acima de R$ 4,8 milhões por ano',

  domain_sub: '{{domain}} no registro.br.',
  dom_registry: 'Registro',
  dom_status: 'Status',
  dom_published: 'Publicado',
  dom_registered: 'Registrado em',
  dom_expires: 'Expira em',
  dom_site: 'Site',
  dom_name: 'Nome',
  dom_title: 'Título',
  dom_description: 'Descrição',
  dom_category: 'Categoria',
  dom_entities: 'Entidades',
  role_registrant: 'Titular',
  role_administrative: 'Contato administrativo',
  role_technical: 'Contato técnico',
  role_billing: 'Contato de cobrança',
  role_other: 'Contato',

  social_sub_one: '1 perfil encontrado. O link abre em uma nova aba.',
  social_sub_other: '{{n}} perfis encontrados. Os links abrem em uma nova aba.',
  new_tab: '(abre em nova aba)',

  site_shot: 'Captura de tela da página inicial',
  site_share: 'Imagem de compartilhamento do site',
  site_lang: 'Idioma',
  site_logo: 'Logo',
  site_logo_alt: 'Logo de {{site}}',
  site_subdomains: 'Visitas por subdomínio',
  site_subdomains_n: 'subdomínios',
  site_keywords: 'Buscas que levam ao site',
  site_keywords_n: 'termos',
  site_others: 'Outros',
  site_engagement: 'Engajamento',
  site_visits: 'Visitas',
  site_pages: 'Páginas por visita',
  site_bounce: 'Taxa de rejeição',
  site_time: 'Tempo no site',
  site_traffic: 'Origem do tráfego',
  site_countries: 'Visitas por país',
  site_history: 'Histórico de visitas',
  site_ads: 'Interesses no Google Ads',
  site_ads_volume: '{{n}} buscas',
  traffic_direct: 'Direto',
  traffic_search: 'Busca',
  traffic_social: 'Redes sociais',
  traffic_referrals: 'Outros sites',
  traffic_paid: 'Anúncios',
  traffic_mail: 'Email',
  traffic_search_organic: 'Busca orgânica',
  traffic_search_paid: 'Busca paga',
  traffic_social_organic: 'Redes sociais',
  traffic_social_paid: 'Redes sociais pagas',
  traffic_display: 'Anúncios em sites',
  traffic_genai: 'IA generativa',
  traffic_affiliate: 'Afiliados',
  traffic_other: 'Outras origens',
  site_ads_more_one: 'Mostrar mais 1 interesse',
  site_ads_more_other: 'Mostrar mais {{n}} interesses',
  site_ads_less: 'Mostrar menos',
  tc_hosting: 'Hospedagem',
  tc_advertising: 'Publicidade',
  tc_analytics: 'Análise e métricas',
  tc_ecommerce: 'Comércio eletrônico',
  tc_font_script: 'Fontes',
  tc_fonts: 'Fontes',
  tc_javascript_frameworks: 'Frameworks JavaScript',
  tc_javascript_libraries: 'Bibliotecas JavaScript',
  tc_javascript_graphics: 'Gráficos JavaScript',
  tc_web_servers: 'Servidores web',
  tc_web_server: 'Servidor web',
  tc_web_frameworks: 'Frameworks web',
  tc_ui_frameworks: 'Frameworks de interface',
  tc_programming_language: 'Linguagem de programação',
  tc_programming_languages: 'Linguagens de programação',
  tc_tag_managers: 'Gerenciadores de tags',
  tc_marketing_automation: 'Automação de marketing',
  tc_payment_processors: 'Pagamentos',
  tc_payments: 'Pagamentos',
  tc_security: 'Segurança',
  tc_video_players: 'Players de vídeo',
  tc_live_chat: 'Chat ao vivo',
  tc_maps: 'Mapas',
  tc_caching: 'Cache',
  tc_reverse_proxies: 'Proxy reverso',
  tc_reverse_proxy: 'Proxy reverso',
  tc_operating_systems: 'Sistemas operacionais',
  tc_databases: 'Bancos de dados',
  tc_miscellaneous: 'Outros',
  tc_widgets: 'Widgets',
  tc_cookie_compliance: 'Consentimento de cookies',
  tc_accessibility: 'Acessibilidade',
  tc_a_b_testing: 'Testes A/B',
  tc_retargeting: 'Remarketing',
  tc_social_login: 'Login social',
  tc_comment_systems: 'Comentários',
  tc_translation: 'Tradução',
  tc_photo_galleries: 'Galerias de fotos',
  tc_customer_data_platform: 'Plataforma de dados de clientes',
  tc_page_builders: 'Construtores de páginas',
  tc_form_builders: 'Formulários',
  tc_rich_text_editors: 'Editores de texto',
  tc_search_engines: 'Busca no site',
  tc_blogs: 'Blogs',
  tc_personalisation: 'Personalização',
  tc_personalization: 'Personalização',
  tc_development: 'Desenvolvimento',
  tc_email: 'Email',
  tc_cdn: 'CDN',
  tc_paas: 'Plataforma em nuvem',
  tc_iaas: 'Infraestrutura em nuvem',
  tc_dns: 'DNS',
  tc_ssl_tls_certificate_authorities: 'Certificados SSL',
  tc_crm: 'CRM',
  tc_lms: 'Ensino a distância',
  tc_affiliate_programs: 'Programas de afiliados',
  tc_loyalty_rewards: 'Fidelidade',
  tc_reviews: 'Avaliações',
  tc_user_onboarding: 'Integração de usuários',
  tc_feature_management: 'Gestão de recursos',
  tc_performance: 'Desempenho',
  tc_rum: 'Monitoramento de usuários',
  tc_surveys: 'Pesquisas',
  tc_appointment_scheduling: 'Agendamento',
  tc_buy_now_pay_later: 'Compre agora, pague depois',
  tc_shipping_carriers: 'Transportadoras',
  tc_static_site_generator: 'Gerador de site estático',
  tc_mobile_frameworks: 'Frameworks mobile',
  tc_containers: 'Contêineres',
  tc_wordpress_plugins: 'Plugins do WordPress',
  tc_wordpress_themes: 'Temas do WordPress',
  // Categorias que faltavam para o Raio-X (decisão 432).
  tc_cms: 'CMS',
  tc_message_boards: 'Fóruns',
  tc_database_managers: 'Gerenciadores de banco de dados',
  tc_documentation: 'Documentação',
  tc_wikis: 'Wikis',
  tc_hosting_panels: 'Painéis de hospedagem',
  tc_issue_trackers: 'Controle de chamados',
  tc_font_scripts: 'Fontes',
  tc_editors: 'Editores',
  tc_webmail: 'Webmail',
  tc_web_server_extensions: 'Extensões de servidor web',
  tc_network_devices: 'Equipamentos de rede',
  tc_media_servers: 'Servidores de mídia',
  tc_webcams: 'Webcams',
  tc_ci: 'Integração contínua',
  tc_control_systems: 'Sistemas de controle',
  tc_remote_access: 'Acesso remoto',
  tc_network_storage: 'Armazenamento em rede',
  tc_feed_readers: 'Leitores de feed',
  tc_dms: 'Gestão de documentos',
  tc_seo: 'SEO',
  tc_accounting: 'Contabilidade',
  tc_cryptominers: 'Mineradores de criptomoeda',
  tc_load_balancers: 'Balanceadores de carga',
  tc_authentication: 'Autenticação',
  tc_geolocation: 'Geolocalização',
  tc_shopify_themes: 'Temas do Shopify',
  tc_drupal_themes: 'Temas do Drupal',
  tc_browser_fingerprinting: 'Identificação do navegador',
  tc_segmentation: 'Segmentação',
  tc_reservations_delivery: 'Reservas e delivery',
  tc_referral_marketing: 'Indicação',
  tc_digital_asset_management: 'Gestão de ativos digitais',
  tc_content_curation: 'Curadoria de conteúdo',
  tc_cart_abandonment: 'Carrinho abandonado',
  tc_shopify_apps: 'Apps do Shopify',
  tc_recruitment_staffing: 'Recrutamento',
  tc_returns: 'Trocas e devoluções',
  tc_livestreaming: 'Transmissão ao vivo',
  tc_ticket_booking: 'Venda de ingressos',
  tc_augmented_reality: 'Realidade aumentada',
  tc_cross_border_ecommerce: 'Comércio eletrônico internacional',
  tc_fulfilment: 'Logística de pedidos',
  tc_ecommerce_frontends: 'Frentes de loja virtual',
  tc_domain_parking: 'Domínio estacionado',
  tc_fundraising_donations: 'Doações',

  tech_sub_one: '1 tecnologia encontrada no site.',
  tech_sub_other: '{{n}} tecnologias encontradas no site.',

  years_one: 'Há 1 ano',
  years_other: 'Há {{n}} anos',
  months_one: 'Há 1 mês',
  months_other: 'Há {{n}} meses',
  less_month: 'Há menos de 1 mês',
  in_months_one: 'Daqui a 1 mês',
  in_months_other: 'Daqui a {{n}} meses',
  in_years_one: 'Daqui a 1 ano',
  in_years_other: 'Daqui a {{n}} anos',
  expired: 'Vencido',

  ct_title: 'Fale conosco',
  ct_text:
    'Dúvida, sugestão ou problema? Escreva aqui. A resposta chega no email da sua conta, <b>{{email}}</b>.',
  ct_text_no_email:
    'Dúvida, sugestão ou problema? Escreva aqui. A resposta chega no email da sua conta.',
  ct_subject: 'Assunto',
  ct_subject_placeholder: 'Escolha um assunto',
  ct_s_access: 'Acesso à ferramenta',
  ct_s_users: 'Adicionar novos usuários',
  ct_s_searches: 'Dúvidas sobre buscas de leads',
  ct_s_renewal: 'Renovar o acesso',
  ct_s_cancel: 'Cancelamento',
  ct_s_problem: 'Reportar um problema',
  ct_s_feature: 'Sugerir funcionalidades',
  ct_s_feedback: 'Feedback sobre a ferramenta',
  ct_s_billing: 'Pagamento, nota fiscal ou reembolso',
  ct_s_referral: 'Programa de recomendação',
  ct_s_other: 'Outro assunto',
  ct_label: 'Mensagem',
  ct_placeholder: 'Conte o que aconteceu ou o que você gostaria de ver no painel.',
  ct_help: 'Central de ajuda',
  ct_send: 'Enviar mensagem',
  ct_sending: 'Enviando…',
  ct_sent: 'Mensagem enviada',
  ct_sent_note: 'Mensagem enviada. A resposta chega no seu email.',
  ct_retry: 'Tentar de novo',
  ct_error: 'Não conseguimos enviar agora. Sua mensagem continua aqui; tente de novo.',
  ct_discard_title: 'Descartar mensagem?',
  ct_discard_text: 'O que você escreveu não fica guardado.',
  ct_keep: 'Continuar escrevendo',
  ct_discard: 'Descartar',

  // Página do LeadExplorer: histórico das análises (proposta de UI de 21/10/2026).
  h_sub_one: '1 empresa analisada pelo CNPJ ou pelo site.',
  h_sub_other: '{{n}} empresas analisadas pelo CNPJ ou pelo site.',
  h_sub_site_one: '1 empresa analisada pelo site.',
  h_sub_site_other: '{{n}} empresas analisadas pelo site.',
  h_sub_empty: 'Empresas que você analisar pelo CNPJ ou pelo site ficam aqui.',
  h_sub_empty_site: 'Empresas que você analisar pelo site ficam aqui.',
  h_count_one: '1 empresa',
  h_count_other: '{{n}} empresas',
  h_count_of: '{{shown}} de {{total}}',
  h_search: 'Buscar por empresa, CNPJ, site ou cidade',
  h_search_site: 'Buscar por empresa, site ou cidade',
  h_table: 'Empresas analisadas',
  h_col_company: 'Empresa',
  h_col_activity: 'Atividade',
  h_col_city: 'Cidade',
  h_col_contacts: 'Contatos',
  h_col_situation: 'Situação',
  h_col_analyzed: 'Analisada',
  h_col_actions: 'Ações',
  h_sort: 'Ordenar por {{col}}',
  h_select_all: 'Selecionar todas as análises',
  h_select: 'Selecionar {{name}}',
  h_partial: 'Parcial',
  h_partial_rf: 'Sem dados da Receita: o site não mostrou um CNPJ',
  h_dossier: 'Dossiê',
  h_dossier_title: 'Tem dossiê com IA',
  h_no_rf: 'sem dado da Receita',
  h_phones_one: '1 telefone',
  h_phones_other: '{{n}} telefones',
  h_emails_one: '1 email',
  h_emails_other: '{{n}} emails',
  h_sit_closed: 'A empresa foi fechada na Receita Federal',
  h_sit_pending: 'A empresa está com pendências na Receita Federal',
  // Empresa francesa (o histórico guarda "FR" no lugar da UF).
  h_sit_closed_fr: 'A empresa foi encerrada no registro francês',
  h_sit_closed_gb: 'A empresa foi dissolvida no registro britânico',
  h_sit_insolvent_gb: 'A empresa está em liquidação ou administração no registro britânico',
  h_failed: 'Não deu certo: a análise não terminou.',
  h_retry: 'Tentar de novo',
  h_today: 'hoje, {{time}}',
  h_yesterday: 'ontem, {{time}}',
  h_analyzed_on: 'Analisada em {{date}}',
  h_more: 'Mais ações de {{name}}',
  h_open: 'Abrir',
  h_reanalyze: 'Analisar de novo',
  h_reanalyze_recent: 'Esta análise é de {{date}}. Dá para refazer a partir de {{until}}.',
  h_copy: 'Copiar CNPJ ou site',
  h_copy_site: 'Copiar site',
  h_copied_cnpj: 'CNPJ copiado',
  h_copied_site: 'Site copiado',
  h_delete: 'Apagar',
  h_none: 'Nenhuma empresa com esse filtro.',
  h_clear: 'Limpar filtros',
  // Painel de filtros do histórico (23/10/2026), no lugar das pílulas.
  hf_button: 'Filtros',
  hf_title: 'Filtros',
  hf_any: 'Tanto faz',
  hf_situation: 'Situação',
  hf_sit_active: 'Ativa',
  hf_sit_unfit: 'Inapta',
  hf_sit_suspended: 'Suspensa',
  hf_sit_closed: 'Baixada',
  hf_sit_null: 'Nula',
  hf_sit_insolvent: 'Em insolvência',
  hf_sit_none: 'Sem dado da Receita',
  hf_need: 'Precisa ter',
  hf_need_phone: 'Telefone',
  hf_need_email: 'Email',
  hf_need_social: 'Redes sociais',
  hf_age: 'Data da análise',
  hf_age_d7: 'Até 7 dias',
  hf_age_d30: 'Até 30 dias',
  hf_age_older30: 'Mais de 30',
  hf_age_note: 'Com mais de 30 dias, a análise pode ser feita de novo.',
  hf_mode: 'Tipo de análise',
  hf_mode_cnpj: 'Pelo CNPJ',
  hf_mode_site: 'Pelo site',
  hf_status: 'Resultado',
  hf_status_ok: 'Completo',
  hf_status_partial: 'Parcial',
  hf_status_failed: 'Com erro',
  hf_dossier: 'Dossiê com IA',
  hf_dossier_with: 'Com',
  hf_dossier_without: 'Sem',
  hf_state: 'Estado ou país',
  hf_activity: 'Atividade',
  hf_show_more: 'Mostrar mais {{n}}',
  hf_show_less: 'Mostrar menos',
  hf_loading: 'Carregando as opções…',
  hf_see_one: 'Ver 1 empresa',
  hf_see_other: 'Ver {{n}} empresas',
  hf_clear: 'Limpar',
  hf_note: 'Os números ao lado das opções contam todas as empresas analisadas.',
  hf_chip_age_d7: 'Últimos 7 dias',
  hf_chip_age_d30: 'Últimos 30 dias',
  hf_chip_age_older30: 'Há mais de 30 dias',
  hf_chip_status_ok: 'Completas',
  hf_chip_status_partial: 'Parciais',
  hf_chip_status_failed: 'Com erro',
  hf_chip_dossier_with: 'Com dossiê',
  hf_chip_dossier_without: 'Sem dossiê',
  hf_chip_need: 'Com {{v}}',
  hf_remove: 'Tirar o filtro {{name}}',
  h_empty_title: 'Nenhuma empresa analisada ainda',
  h_empty_text:
    'Digite um CNPJ ou um site e veja quem é a empresa, seus contatos públicos e como abordá-la. Cada análise fica guardada aqui.',
  h_empty_text_site:
    'Digite um site e veja quem é a empresa, seus contatos públicos e como abordá-la. Cada análise fica guardada aqui.',
  h_selected_one: '1 análise selecionada',
  h_selected_other: '{{n}} análises selecionadas',
  h_selection: 'Ações das análises selecionadas',
  h_clear_selection: 'Limpar seleção',
  h_del_title_one: 'Apagar a análise de {{name}}?',
  h_del_title_other: 'Apagar {{n}} análises?',
  h_del_text_one: 'Ela sai desta lista. Para vê-la de novo, analise a empresa outra vez.',
  h_del_text_other: 'Elas saem desta lista. Para vê-las de novo, analise as empresas outra vez.',
  h_del_keep: 'Voltar',
  h_del_more_one: 'e mais 1 análise',
  h_del_more_other: 'e mais {{n}} análises',
  h_deleting: 'Apagando…',
  h_deleted_one: 'Análise apagada',
  h_deleted_other: '{{n}} análises apagadas',
  h_del_error: 'Não conseguimos apagar agora. Tente de novo.',
  h_load_error: 'Não conseguimos carregar suas análises.',
  h_more_rows_one: 'Mostrar mais 1 análise',
  h_more_rows_other: 'Mostrar mais {{n}} análises',
  h_already: 'Você analisou {{what}} {{when}}.',
  h_this_site: 'este site',
  h_this_cnpj: 'este CNPJ',
  h_already_redo: 'Analisar de novo atualiza todos os dados.',
  h_when_today: 'hoje',
  h_when_yesterday: 'ontem',
  h_when_date: 'em {{date}}',
  h_already_view: 'Ver o resultado',
  h_saved_loading: 'Abrindo a análise guardada…',
  h_saved_error: 'Não conseguimos abrir esta análise. Tente de novo.',

  // Baixar análises (fase 2).
  d_title_one: 'Baixar a análise de {{name}}',
  d_title_other: 'Baixar {{n}} análises',
  d_includes: 'Leva tudo o que a análise mostra, inclusive contatos e sócios.',
  d_formats: 'Formato do arquivo',
  d_pdf: 'PDF',
  d_json: 'JSON',
  d_pdf_hint_one: 'A análise completa, para ler, imprimir ou enviar.',
  d_pdf_hint_other: 'Um PDF por empresa, num arquivo .zip.',
  d_json_hint_one: 'Todos os dados da análise, para outros sistemas.',
  d_json_hint_other: 'Todas as análises num arquivo só, para outros sistemas.',
  d_too_many: 'Dá para baixar até {{max}} análises por vez. Desmarque {{n}} para continuar.',
  d_skipped_one: '1 análise que não deu certo fica de fora.',
  d_skipped_other: '{{n}} análises que não deram certo ficam de fora.',
  d_unit_one: 'análise',
  d_unit_other: 'análises',
  d_preparing: 'Preparando {{done}} de {{total}}…',
  d_error: 'Não conseguimos preparar o arquivo. Tente de novo.',
  d_download: 'Baixar',
  d_done_one: 'Análise baixada',
  d_done_other: '{{n}} análises baixadas',
  d_missing_one: '1 não pôde ser aberta e ficou de fora',
  d_missing_other: '{{n}} não puderam ser abertas e ficaram de fora',
  d_generated: 'Gerado em {{date}}.',
  d_page: 'Página {{n}} de {{total}}',
  // Analisar de novo várias (fase 3b).
  r_title_one: 'Analisar de novo {{name}}?',
  r_title_other: 'Analisar de novo {{n}} análises?',
  r_text: 'Os dados públicos e a estratégia são refeitos no servidor, poucas análises de cada vez. Dá para sair desta tela enquanto isso.',
  r_ceiling: 'Cada análise com mais de 30 dias conta no limite de reanálises do dia.',
  r_recent_one: '1 análise tem menos de 30 dias e fica como está.',
  r_recent_other: '{{n}} análises têm menos de 30 dias e ficam como estão.',
  r_all_recent: 'Todas as escolhidas têm menos de 30 dias e ficam como estão.',
  r_too_many: 'Dá para analisar de novo até {{max}} por vez. Desmarque {{n}} para continuar.',
  r_confirm: 'Analisar de novo',
  r_sending: 'Enviando…',
  r_error: 'Não conseguimos começar agora. Tente de novo.',
  r_queued_one: '1 análise entrou na fila.',
  r_queued_other: '{{n}} análises entraram na fila.',
  r_nothing: 'Nenhuma análise entrou na fila.',
  r_limit_one: '1 ficou de fora: as reanálises de hoje acabaram.',
  r_limit_other: '{{n}} ficaram de fora: as reanálises de hoje acabaram.',
  r_busy_one: '1 já estava na fila.',
  r_busy_other: '{{n}} já estavam na fila.',
  r_badge_queued: 'Na fila',
  r_badge_running: 'Analisando…',
  // Sugestões de site no campo.
  s_list: 'Sugestões de sites',
  s_count_one: '1 sugestão de site. Use as setas para escolher.',
  s_count_other: '{{n}} sugestões de site. Use as setas para escolher.',
  // Aba LinkedIn.
  tab_linkedin: 'LinkedIn',
  li_sub: 'Página da empresa no LinkedIn, encontrada pelo site.',
  li_industry: 'Setor',
  li_size: 'Funcionários',
  li_size_range: '{{from}} a {{to}} funcionários',
  li_size_plus: 'Mais de {{n}} funcionários',
  li_founded: 'Fundação',
  li_place: 'Sede',
  li_website: 'Site',
  li_page: 'Página',
  li_name: 'Nome',
  // Aba do registro francês (SIREN, SIRET ou IVA escrito no site).
  tab_fr: 'Registro na França',
  fr_sub:
    'Cadastro da empresa no registro público da França (INSEE e RNE), pelo número escrito no site. Atividade, natureza jurídica e cargos aparecem como no registro, em francês.',
  fr_open: 'Ver no registro',
  fr_active: 'Ativa',
  fr_closed: 'Encerrada',
  fr_closed_on: 'Encerrada em {{date}}',
  fr_created: 'Criação',
  fr_category: 'Porte',
  fr_cat_pme: 'Pequena ou média empresa',
  fr_cat_pme_rev: 'PME: menos de 250 funcionários',
  fr_cat_eti: 'Empresa de porte intermediário',
  fr_cat_eti_rev: 'ETI: de 250 a 4.999 funcionários',
  fr_cat_ge: 'Grande empresa',
  fr_cat_ge_rev: 'GE: 5.000 funcionários ou mais',
  fr_employees: 'Funcionários',
  fr_employees_range: '{{from}} a {{to}}',
  fr_employees_plus: '{{n}} ou mais',
  fr_employees_none: 'Nenhum',
  fr_employees_year: 'Dado de {{year}}',
  fr_revenue: 'Faturamento',
  fr_net_income: 'Resultado líquido',
  fr_balance_year: 'Balanço de {{year}}',
  fr_siren: 'SIREN',
  fr_siret: 'SIRET da sede',
  fr_tva: 'IVA intracomunitário',
  fr_trade: 'Nome comercial',
  fr_address: 'Sede',
  fr_country: 'França',
  fr_establishments: 'Estabelecimentos abertos',
  fr_activity: 'Atividade principal (NAF)',
  fr_directors: 'Dirigentes',
  fr_director_company: 'Empresa',
  fr_active_side: 'Ativa no registro francês',
  fr_closed_side: 'Encerrada no registro francês',
  // Aba do registro britânico (número da Companies House escrito no site).
  tab_gb: 'Registro no Reino Unido',
  gb_sub:
    'Cadastro da empresa na Companies House, o registro público do Reino Unido, pelo número escrito no site. Tipo, atividades, balanço e cargos aparecem como no registro, em inglês.',
  gb_number: 'Company number',
  gb_created: 'Abertura',
  gb_strike_off: 'Pedido de baixa em andamento',
  gb_st_active: 'Ativa',
  gb_st_dissolved: 'Dissolvida',
  gb_st_liquidation: 'Em liquidação',
  gb_st_administration: 'Em administração judicial',
  gb_st_receivership: 'Com interventor nomeado',
  gb_st_arrangement: 'Em acordo com credores',
  gb_st_insolvency: 'Em processo de insolvência',
  gb_st_closed: 'Encerrada',
  gb_closed_on: 'Encerrada em {{date}}',
  gb_size: 'Porte',
  gb_size_micro: 'Microempresa',
  gb_size_micro_rev: 'Balanço de microempresa: até 10 funcionários',
  gb_size_small: 'Pequena empresa',
  gb_size_small_rev: 'Balanço de pequena empresa: até 50 funcionários',
  gb_size_medium: 'Média empresa',
  gb_size_medium_rev: 'Balanço de média empresa: até 250 funcionários',
  gb_size_dormant: 'Sem atividade',
  gb_size_dormant_rev: 'Balanço de empresa sem movimento (dormant)',
  gb_accounts: 'Último balanço',
  gb_accounts_until: 'Fechado em {{date}}',
  gb_accounts_overdue: 'Balanço atrasado',
  gb_type: 'Tipo',
  gb_previous: 'Nomes anteriores',
  gb_previous_until: 'até {{date}}',
  gb_insolvency_history: 'Histórico de insolvência',
  gb_yes: 'Sim',
  gb_activities: 'Atividades (SIC)',
  gb_directors: 'Diretores e secretários',
  gb_since: 'Desde {{date}}',
  gb_owners: 'Quem controla a empresa (PSC)',
  gb_band_range: '{{from}}% a {{to}}%',
  gb_band_plus: '{{from}}% ou mais',
  gb_ctrl_shares: 'Ações: {{band}}',
  gb_ctrl_votes: 'Votos: {{band}}',
  gb_ctrl_surplus: 'Bens restantes: {{band}}',
  gb_ctrl_directors: 'Nomeia ou destitui diretores',
  gb_ctrl_influence: 'Influência significativa',
  gb_company: 'Empresa',
  gb_country: 'Reino Unido',
  gb_side: '{{status}} no registro britânico',
  // Topo do resultado e contatos encontrados (Claude Design, 23/10/2026).
  top_no_site: 'Sem site encontrado',
  top_linkedin: 'LinkedIn',
  top_facts: 'Resumo do cadastro',
  top_since: 'Desde {{year}} · {{age}}',
  age_years_one: '1 ano',
  age_years_other: '{{n}} anos',
  age_months_one: '1 mês',
  age_months_other: '{{n}} meses',
  top_copy_id: 'Copiar {{id}}',
  top_copied_id: '{{id}} copiado',
  tab_contacts: 'Contatos',
  cx_sources_one: '1 fonte',
  cx_sources_other: '{{n}} fontes',
  cx_copy_all: 'Copiar todos',
  cx_copied_all: 'Copiados',
  cx_copied_all_msg: 'Contatos copiados.',
  cx_kind_mobile: 'Celular',
  cx_kind_personal: 'Pessoal',
  cx_kind_landline: 'Fixo',
  cx_kind_generic: 'Genérico',
  cx_src_rf: 'Receita Federal',
  cx_src_site: 'Site',
  cx_src_domain: 'Domínio',
  cx_src_and: '{{a}} e {{b}}',
  cx_whatsapp: 'Conversar no WhatsApp com {{v}}',
  cx_tel: 'Telefone',
  cx_mail: 'Email',
  cx_copy_tel: 'Copiar telefone {{v}}',
  cx_copy_mail: 'Copiar email {{v}}',
  cx_copied_tel: 'Telefone copiado',
  cx_copied_mail: 'Email copiado',
  cx_copied_msg: '{{what}} copiado.',
  cx_found_one: '1 contato público encontrado',
  cx_found_other: '{{n}} contatos públicos encontrados',
  cx_sub: '{{found}} em {{sources}}.',
  cx_sub_none: 'Nenhum contato público encontrado para esta empresa.',
  cx_filter: 'Filtrar contatos',
  cx_f_all: 'Todos',
  cx_f_tel: 'Telefones',
  cx_f_mail: 'Emails',
  cx_order: 'Ordem: celulares primeiro, depois emails de pessoas, telefones fixos e emails genéricos. Contatos repetidos em mais de uma fonte aparecem uma vez só.',
  cx_where: 'Onde procuramos',
  cx_where_rf: 'Receita Federal',
  cx_where_domain: 'Registro do domínio',
  cx_where_site: 'Site e redes sociais',
  cx_res_none: 'Sem telefone e sem email',
  cx_res_found_one: '1 contato',
  cx_res_found_other: '{{n}} contatos',
  cx_res_no_cnpj: 'Sem CNPJ',
  cx_res_no_domain: 'Fora do registro.br',
  cx_res_no_site: 'Sem site',
  cx_todo: 'O que dá para fazer',
  cx_todo_partners: 'Veja os sócios na guia Receita Federal e busque por eles no LinkedIn.',
  cx_todo_google: 'Busque o nome no Google ou no Google Maps: negócios locais costumam ter telefone lá.',
  cx_todo_later: 'Faça uma nova análise mais tarde: contatos novos aparecem quando a empresa cria site ou perfis.',

  // Raio-X: tecnologias e DNS (decisões 427 a 433).
  stack_access_frame: 'Acesso aos sites',
  stack_by_dns: 'pelo DNS',
  stack_implied: 'Indicada por {{name}}',
  stack_version: 'Versão {{v}}',
  stack_why: 'Por que aparece',
  stack_ev_headers: 'Cabeçalho {{key}}',
  stack_ev_meta: 'Meta {{key}}',
  stack_ev_cookies: 'Cookie {{key}}',
  stack_ev_script: 'Script',
  stack_ev_scripts: 'Código de script',
  stack_ev_html: 'HTML da página',
  stack_ev_js: 'Variável {{key}}',
  stack_ev_dom: 'Elemento {{key}}',
  stack_ev_xhr: 'Chamada a',
  stack_ev_url: 'Endereço',
  stack_ev_text: 'Texto da página',
  stack_ev_css: 'CSS',
  stack_ev_dns: 'Registro {{key}}',
  stack_ev_other: 'Sinal',
  stack_open_site: 'Site de {{name}}',
  domain_sub_dns: 'DNS público de {{domain}}.',
  dns_title: 'DNS',
  dns_email: 'Email',
  dns_nameservers: 'Servidores de DNS',
  dns_address: 'Endereço do site',
  dns_services: 'Serviços verificados',
  dns_records: 'Registros',
  dns_col_type: 'Tipo',
  dns_col_name: 'Nome',
  dns_col_value: 'Valor',
  dns_more_one: 'Ver mais 1 registro',
  dns_more_other: 'Ver mais {{n}} registros',
  dns_less: 'Ver menos',
  dns_subdomains: 'Subdomínios',
  dns_sub_count_one: '1 subdomínio',
  dns_sub_count_other: '{{n}} subdomínios',
  dns_sub_note: 'Pelos certificados públicos do domínio, conferidos no DNS.',
  dns_sub_error:
    'Os certificados públicos não responderam agora. Analise de novo depois para ver os subdomínios.',
  dns_sub_none: 'Nenhum subdomínio nos certificados públicos.',
} as const;

export type LxKey = keyof typeof LX_PT;
type Vars = Record<string, string | number>;

export function useLxText() {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language ?? 'pt').slice(0, 2);
  const locale = localeOf(lang);

  const tx = useCallback(
    (key: LxKey, vars?: Vars): string => {
      const out: unknown = t(`lx_${key}`, {
        defaultValue: LX_PT[key],
        ...vars,
        interpolation: { escapeValue: false },
      });
      const fill = (text: string): string =>
        text.replace(/\{\{(\w+)\}\}/g, (m, name: string) =>
          vars && name in vars ? String(vars[name]) : m,
        );
      return fill(typeof out === 'string' ? out : LX_PT[key]);
    },
    [t],
  );
  /** Chave `<base>_one`/`<base>_other` com `{{n}}` já formatado. */
  const plural = useCallback(
    (base: string, n: number): string =>
      tx(`${base}_${(lang === 'fr' ? n <= 1 : n === 1) ? 'one' : 'other'}` as LxKey, {
        n: formatNumber(n, locale),
      }),
    [lang, locale, tx],
  );
  const number = useCallback((n: number): string => formatNumber(n, locale), [locale]);
  const longDate = useCallback(
    (value: Date | string): string => {
      const d = toDate(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(d);
    },
    [locale],
  );
  /** "Há 11 anos", "Daqui a 4 meses". */
  const relative = useCallback(
    (value: Date | string): string => {
      const d = toDate(value);
      if (Number.isNaN(d.getTime())) return '';
      const now = new Date();
      const future = d.getTime() > now.getTime();
      const [a, b] = future ? [now, d] : [d, now];
      let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
      if (b.getDate() < a.getDate()) months -= 1;
      if (months >= 12) return plural(future ? 'in_years' : 'years', Math.floor(months / 12));
      if (months >= 1) return plural(future ? 'in_months' : 'months', months);
      return future ? plural('in_months', 1) : tx('less_month');
    },
    [plural, tx],
  );
  /** Categoria do site ("finance/finance") no idioma da pessoa: "Finanças". */
  const category = useCallback(
    (value: unknown): string | null => {
      const raw = asText(value);
      if (!raw) return null;
      const out: unknown = t(raw, { defaultValue: '' });
      const text = typeof out === 'string' && out.trim() ? out : raw.replace(/_/g, ' ');
      const parts: string[] = [];
      for (const part of text
        .split('/')
        .map((p) => p.trim())
        .filter(Boolean)) {
        const nice = part.charAt(0).toUpperCase() + part.slice(1);
        if (!parts.some((p) => p.toLowerCase() === nice.toLowerCase())) parts.push(nice);
      }
      return parts.join(' › ') || null;
    },
    [t],
  );
  /** Grupo de tecnologias ("Hosting") no idioma da pessoa; grupo desconhecido fica como veio. */
  const techGroup = useCallback(
    (name: string): string => {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const key = `tc_${slug}`;
      return key in LX_PT ? tx(key as LxKey) : name;
    },
    [tx],
  );
  /** Quando a cota renova: "amanhã" ou o dia da semana ("segunda-feira"), no idioma da tela. */
  const renewal = useCallback(
    (resetAt: string | Date, now = new Date()): string => {
      const at = toDate(resetAt);
      const days = Math.round((at.getTime() - now.getTime()) / 86_400_000);
      try {
        if (days <= 1) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(1, 'day');
        return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(at);
      } catch {
        return at.toLocaleDateString();
      }
    },
    [locale],
  );
  return { tx, plural, number, longDate, relative, category, techGroup, renewal, lang, locale };
}
